import type { ReactNode } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import type { SourceOption } from '../_lib/source-option'
import { SourceGlyph } from './source-glyph'

/**
 * One source option as a button — the shared mechanism under the library
 * cell's sideways slide-out and the card switcher's drop-down (LG-362 §2).
 * Each surface keeps only what genuinely differs: its reveal geometry and
 * its label verb ("Add with …" / "Assign from …"). The tooltip + button +
 * glyph structure lives here once — before it was shared, the badge-covers-
 * icon and prose-margin fixes each had to be reasoned about per surface, and
 * the next affordance tweak would have landed twice or drifted.
 */
export function SourceOptionButton({
  option,
  label,
  tooltip,
  disabled,
  onClick,
}: {
  option: SourceOption
  /** The full accessible action, verb included — e.g. "Add with LeetCode · 1". */
  label: string
  tooltip: ReactNode
  disabled: boolean
  onClick: () => void
}): ReactNode {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block" tabIndex={disabled ? 0 : undefined}>
          <button
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            className="block cursor-pointer rounded border-0 bg-transparent p-0 hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-45"
          >
            <SourceGlyph displayName={option.displayName} icon={option.icon} />
          </button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

/**
 * The "N other sources" superscript on a resting mark. Presentational only
 * (`aria-hidden`): the option buttons name every source themselves. The
 * carrier passes its own hide rule via `className` — the library cell hides
 * it on hover/focus-within/touch, the card switcher while its popover is
 * open — because when to yield to the revealed options is the one thing the
 * two surfaces decide differently.
 */
export function SourceCountBadge({
  count,
  className = '',
}: {
  count: number
  className?: string
}): ReactNode {
  return (
    <span
      aria-hidden="true"
      className={`absolute -top-1.5 -right-1 text-[8px] leading-none font-bold text-(--cd-text-muted) transition-opacity ${className}`}
    >
      {count}
    </span>
  )
}
